import React, { FC, KeyboardEvent, MouseEvent, useEffect, useState } from 'react';
import {
  AlertColor,
  Box,
  CircularProgress,
  InputAdornment,
  Link,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import KeyboardCapslockIcon from '@mui/icons-material/KeyboardCapslock';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import PrimaryButton from '../../../components/Button/PrimaryButton';
import InputField from '../../../components/InputField';
import { URLS } from '../../../utils/constants/urls';
import axiosInstance from '../../../utils/axios';
import RecaptchaNotice from '../../../components/Captcha/RecaptchaNotice';
import strings from '../../../common/Translation/Translate';
import { routes } from 'utils/constants/routes';
import { RECAPTCHA_ACTIONS } from '../../../utils/recaptchaActions';
import { Validators } from '../../../utils/validators';

interface EmailPasswordLoginProps {
  setMessage: (message: { display: boolean; severity: AlertColor | null; message: string }) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  handleLoginSuccess: (data: any, identifier: string) => void;
  Navigate: any;
}

const fieldSx = {
  mb: 2.5,
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fff',
  },
  '& .MuiInputLabel-root': {
    fontWeight: 600,
    color: '#1e293b',
  },
};

const EmailPasswordLogin: FC<EmailPasswordLoginProps> = ({
  setMessage,
  loading,
  setLoading,
  handleLoginSuccess,
  Navigate,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [hasError, setHasError] = useState(false);
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  useEffect(() => {
    const syncCapsLock = (event: Event) => {
      const keyboardEvent = event as globalThis.KeyboardEvent;
      if (typeof keyboardEvent.getModifierState === 'function') {
        setCapsLockOn(keyboardEvent.getModifierState('CapsLock'));
      }
    };
    window.addEventListener('keydown', syncCapsLock);
    window.addEventListener('keyup', syncCapsLock);
    return () => {
      window.removeEventListener('keydown', syncCapsLock);
      window.removeEventListener('keyup', syncCapsLock);
    };
  }, []);

  const handleCapsLock = (event: KeyboardEvent<HTMLInputElement>) => {
    setCapsLockOn(event.getModifierState('CapsLock'));
  };

  const handleLogin = async (e: MouseEvent<HTMLButtonElement> | React.FormEvent) => {
    e.preventDefault();
    const emailValidation = username.trim()
      ? Validators.validateEmail(username, strings.invalid_email_message)
      : { error: true };
    const emailInvalid = !username.trim() || !!emailValidation?.error;
    const passwordInvalid = !password.trim();
    setUsernameError(emailInvalid);
    setPasswordError(passwordInvalid);
    setHasError(emailInvalid || passwordInvalid);

    if (emailInvalid || passwordInvalid) {
      return;
    }

    try {
      if (!executeRecaptcha) {
        setMessage({
          display: true,
          severity: 'warning',
          message: 'reCAPTCHA is not ready',
        });
        return;
      }
      setLoading(true);
      const captchaRes = await executeRecaptcha(RECAPTCHA_ACTIONS.login);
      const { status, data } = await axiosInstance.post(URLS.login, {
        loginType: 'EMAIL_PASSWORD',
        username,
        password,
        captchaResponse: {
          captchaRes,
          captchaAction: RECAPTCHA_ACTIONS.login,
        },
      });
      if (status === 200) {
        handleLoginSuccess(data, username);
      }
    } catch (error: any) {
      if (error) {
        if (error?.response?.status === 403) {
          Navigate(`${routes.setPasswordAfterUpgrade}?email=${encodeURIComponent(username)}`, {
            replace: true,
          });
          return;
        }
        const errorMessage = error.response ? error.response.data.error_description : error.message;
        setMessage({
          display: true,
          severity: 'warning',
          message: errorMessage || error.response?.data?.message,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleLogin as any} className="login-form-fields">
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b', mb: 0.75 }}>
        Email address
      </Typography>
      <InputField
        autoFocus
        label=" "
        field="username"
        inputValue={username}
        textChange={value => {
          setUsername(value);
          setUsernameError(false);
        }}
        hasError={hasError}
        fieldError={usernameError}
        validators={[
          { check: Validators.validateMandatoryField, message: strings.requiredText },
          { check: Validators.validateEmail, message: strings.invalid_email_message },
        ]}
        variant="outlined"
        size="medium"
        InputLabelProps={{ shrink: false }}
        sx={{
          ...fieldSx,
          '& .MuiInputLabel-root': { display: 'none' },
          '& .MuiOutlinedInput-notchedOutline legend': { width: 0 },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <MailOutlineIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
            </InputAdornment>
          ),
        }}
        onKeyUp={handleCapsLock}
        onKeyDown={handleCapsLock}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>Password</Typography>
        <Link
          data-testid="forget"
          underline="hover"
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: 'primary.main',
            cursor: 'pointer',
          }}
          onClick={() => Navigate('/forgot-password')}>
          Forgot password?
        </Link>
      </Box>
      <InputField
        label=" "
        field="password"
        inputValue={password}
        textChange={value => {
          setPassword(value);
          setPasswordError(false);
        }}
        hasError={hasError}
        fieldError={passwordError}
        typeValue="password"
        validators={[{ check: Validators.validateMandatoryField, message: strings.requiredText }]}
        variant="outlined"
        size="medium"
        InputLabelProps={{ shrink: false }}
        sx={{
          ...fieldSx,
          mb: 0.5,
          '& .MuiInputLabel-root': { display: 'none' },
          '& .MuiOutlinedInput-notchedOutline legend': { width: 0 },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockOutlinedIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
            </InputAdornment>
          ),
        }}
        onKeyUp={handleCapsLock}
        onKeyDown={handleCapsLock}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          mb: 3,
          mt: 0.5,
          color: capsLockOn ? '#64748b' : '#94a3b8',
        }}>
        <KeyboardCapslockIcon sx={{ fontSize: 16 }} />
        <Typography sx={{ fontSize: '0.75rem' }}>
          Caps Lock is {capsLockOn ? 'on' : 'off'}
        </Typography>
      </Box>

      <PrimaryButton
        disabled={loading}
        sx={{
          py: 1.4,
          fontSize: '1rem',
          fontWeight: 600,
          textTransform: 'none',
          borderRadius: '8px',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        }}
        onClick={handleLogin}
        type="submit"
        fullWidth>
        {loading && <CircularProgress sx={{ mr: 1, color: '#fff' }} size={20} />}
        Sign in
      </PrimaryButton>
      <RecaptchaNotice />
    </form>
  );
};

export default EmailPasswordLogin;
